import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import api from '../services/api';

const COLORS = ['#1D9E75', '#3B82F6', '#EF9F27', '#EC4899', '#8B5CF6', '#14B8A6'];

const cardStyle = {
  background: 'white',
  border: '1px solid #E8E7E3',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
};

const cardTitleStyle = {
  fontSize: '15px',
  fontWeight: '600',
  color: '#1F2937',
  margin: '0 0 4px',
};

const cardSubStyle = {
  fontSize: '12px',
  color: '#73726C',
  margin: '0 0 16px',
};

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [activity, setActivity] = useState([]);
  const [features, setFeatures] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [errors, setErrors] = useState([]);
  const [topClients, setTopClients] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, ac, ft, fu, er, tc] = await Promise.all([
        api.get('/admin/analytics/overview'),
        api.get('/admin/analytics/activity?days=30'),
        api.get('/admin/analytics/features'),
        api.get('/admin/analytics/funnel'),
        api.get('/admin/analytics/errors?days=7'),
        api.get('/admin/analytics/top-clients'),
      ]);
      if (ov?.success) setOverview(ov.overview);
      if (ac?.success) setActivity(ac.activity);
      if (ft?.success) setFeatures(ft.features);
      if (fu?.success) setFunnel(fu.funnel);
      if (er?.success) setErrors(er.errors);
      if (tc?.success) setTopClients(tc.clients);
    } catch (err) {
      console.error('Erreur chargement analytics:', err);
      setError('Impossible de charger les statistiques.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div style={{ fontSize: '14px', color: '#73726C' }}>⏳ Chargement des statistiques...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={cardStyle}>
        <div style={{ color: '#A32D2D', fontSize: '14px' }}>⚠️ {error}</div>
        <button
          onClick={fetchAll}
          style={{ marginTop: '12px', padding: '8px 16px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  const kpis = [
    {
      icon: '👥',
      bg: '#E1F5EE',
      label: 'Clients actifs (7j / 30j)',
      value: `${overview?.active_clients_7d ?? 0} / ${overview?.active_clients_30d ?? 0}`,
      sub: `${overview?.visitors_30d ?? 0} visiteurs landing (30j)`,
    },
    {
      icon: '💰',
      bg: '#E8F4FD',
      label: 'MRR',
      value: `${(overview?.mrr_simple ?? 0).toLocaleString('fr-FR')} €`,
      sub: `Normalisé : ${(overview?.mrr_normalise ?? 0).toLocaleString('fr-FR')} € / mois`,
    },
    {
      icon: '📈',
      bg: '#FEF3C7',
      label: 'Conversion essai → payant',
      value: `${overview?.conversion_essai_actif ?? 0} %`,
      sub: `${overview?.nb_abonnes_payants ?? 0} abonnés payants`,
    },
    {
      icon: '🚨',
      bg: '#FCEBEB',
      label: 'Erreurs frontend (7j)',
      value: overview?.errors_7d ?? 0,
      sub: `${overview?.signups_30d ?? 0} inscriptions (30j)`,
    },
  ];

  const featurePieData = features.slice(0, 6).map((f) => ({
    name: f.name,
    value: f.value,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Barre d'actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={fetchAll}
          style={{ padding: '8px 16px', background: 'white', color: '#1F2937', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}
        >
          🔄 Actualiser
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
        {kpis.map((k) => (
          <div key={k.label} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ fontSize: '32px', padding: '12px', background: k.bg, borderRadius: '12px' }}>{k.icon}</div>
              <div>
                <div style={{ fontSize: '13px', color: '#73726C', fontWeight: '500' }}>{k.label}</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#1F2937', marginTop: '4px', lineHeight: 1.1 }}>{k.value}</div>
                <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '6px' }}>{k.sub}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Graphiques ligne 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>📊 Activité des 30 derniers jours</h3>
          <p style={cardSubStyle}>Vues de pages, features et connexions par jour.</p>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={activity} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E7E3" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#73726C' }} tickFormatter={(d) => d.slice(8, 10)} minTickGap={20} />
                <YAxis tick={{ fontSize: 11, fill: '#73726C' }} allowDecimals={false} />
                <Tooltip labelFormatter={(label) => `Date : ${label}`} contentStyle={{ borderRadius: '10px', border: '1px solid #E8E7E3' }} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Area type="monotone" dataKey="page_views" name="Vues de pages" stackId="1" stroke="#1D9E75" fill="#1D9E75" fillOpacity={0.25} />
                <Area type="monotone" dataKey="feature_uses" name="Features" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.25} />
                <Area type="monotone" dataKey="logins" name="Connexions" stackId="1" stroke="#EF9F27" fill="#EF9F27" fillOpacity={0.35} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>🧩 Top features (30j)</h3>
          <p style={cardSubStyle}>Actions réelles (mois en cours) + navigations des autres modules.</p>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={featurePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(entry) => entry.name}>
                  {featurePieData.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E8E7E3' }} formatter={(value) => [value, 'Utilisations']} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Graphiques ligne 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>📈 Funnel de conversion (90j)</h3>
          <p style={cardSubStyle}>Visiteurs → inscrits → essais → payants.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {funnel.map((step, idx) => {
              const max = Math.max(...funnel.map((s) => s.value), 1);
              const pct = (step.value / max) * 100;
              return (
                <div key={step.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '5px' }}>
                    <span style={{ fontWeight: '500', color: '#1F2937' }}>{step.label}</span>
                    <span style={{ color: '#73726C' }}>
                      {step.value}
                      {idx > 0 && step.rate !== undefined ? ` (${step.rate} %)` : ''}
                    </span>
                  </div>
                  <div style={{ background: '#F5F4ED', borderRadius: '8px', height: '22px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${Math.max(pct, 2)}%`,
                        height: '100%',
                        background: idx === funnel.length - 1 ? '#1D9E75' : COLORS[idx % COLORS.length],
                        borderRadius: '8px',
                        opacity: 0.85,
                        transition: 'width 0.5s',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>🏆 Top 5 clients les plus actifs</h3>
          <p style={cardSubStyle}>Compteurs d'usage du mois en cours.</p>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={topClients} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E7E3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#73726C' }} allowDecimals={false} />
                <YAxis type="category" dataKey="nom" width={110} tick={{ fontSize: 11, fill: '#73726C' }} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E8E7E3' }} />
                <Bar dataKey="total" name="Actions (mois)" fill="#1D9E75" radius={[0, 6, 6, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px' }}>
            {topClients.map((c) => (
              <div key={c.id} style={{ fontSize: '12px', color: '#73726C' }}>
                <strong style={{ color: '#1F2937' }}>{c.nom}</strong> · OCR {c.ocr} · Chat {c.chat} · Docs {c.documents}
              </div>
            ))}
            {topClients.length === 0 && <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Aucune activité ce mois-ci.</div>}
          </div>
        </div>
      </div>

      {/* Erreurs récentes */}
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>🚨 Erreurs frontend récentes (7j)</h3>
        <p style={cardSubStyle}>Les 10 dernières erreurs rencontrées par les utilisateurs.</p>
        {errors.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Aucune erreur enregistrée. 🎉</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#73726C', borderBottom: '1px solid #E8E7E3' }}>
                  <th style={{ padding: '10px 12px', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '10px 12px', fontWeight: '600' }}>Client</th>
                  <th style={{ padding: '10px 12px', fontWeight: '600' }}>Module</th>
                  <th style={{ padding: '10px 12px', fontWeight: '600' }}>Erreur</th>
                  <th style={{ padding: '10px 12px', fontWeight: '600' }}>Page</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((e) => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #F0EFEA' }}>
                    <td style={{ padding: '10px 12px', color: '#73726C', whiteSpace: 'nowrap' }}>
                      {e.date ? new Date(e.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#1F2937' }}>{e.client_id || 'Anonyme'}</td>
                    <td style={{ padding: '10px 12px', color: '#1F2937' }}>{e.event_name}</td>
                    <td style={{ padding: '10px 12px', color: '#A32D2D', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.error_msg}>
                      {e.error_msg || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#73726C' }}>{e.path || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
